import Phaser from 'phaser';

/** Texture keys for the hub wallet and the trophy-road and win-streak cards. */
export const HUB_ICON = {
  acornCurrency: 'icon-acorn-currency',
  honeyCurrency: 'icon-honey-currency',
  trophy: 'icon-trophy',
  streak: 'icon-streak',
} as const;

export const HUB_ICONS = [
  { key: HUB_ICON.acornCurrency, path: 'assets/icons/cc_icon_acorn_currency.png' },
  { key: HUB_ICON.honeyCurrency, path: 'assets/icons/cc_icon_honey_currency.png' },
  { key: HUB_ICON.trophy, path: 'assets/icons/cc_icon_trophy.png' },
  { key: HUB_ICON.streak, path: 'assets/icons/cc_icon_streak.png' },
] as const;

const MAX_ATTEMPTS = 3;
const ATTEMPT_MS = 12_000;

/** The first fetch uses the plain URL so a warm cache can hit. Later tries bypass a bad entry. */
export function hubIconUrl(path: string, attempt: number): string {
  if (attempt <= 1) return path;
  const join = path.includes('?') ? '&' : '?';
  return `${path}${join}reload=${attempt}`;
}

export function missingHubIcons(textures: { exists(key: string): boolean }): (typeof HUB_ICONS)[number][] {
  return HUB_ICONS.filter((icon) => !textures.exists(icon.key));
}

/** Painted stand-ins so a failed file never leaves the green missing-texture box. */
export function installHubIconFallbacks(scene: Phaser.Scene): void {
  for (const icon of HUB_ICONS) {
    if (scene.textures.exists(icon.key)) continue;
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    paintFallback(g, icon.key);
    g.generateTexture(icon.key, 64, 64);
    g.destroy();
  }
}

function paintFallback(g: Phaser.GameObjects.Graphics, key: string): void {
  if (key === HUB_ICON.honeyCurrency) {
    g.fillStyle(0xe7a020, 1);
    g.fillRoundedRect(22, 16, 20, 16, 6);
    g.fillStyle(0xf0c14a, 1);
    g.fillCircle(32, 38, 16);
    return;
  }
  if (key === HUB_ICON.trophy) {
    g.fillStyle(0xf0c14a, 1);
    g.fillRoundedRect(18, 14, 28, 18, 6);
    g.fillRect(28, 32, 8, 12);
    g.fillRoundedRect(16, 44, 32, 8, 3);
    return;
  }
  if (key === HUB_ICON.streak) {
    g.fillStyle(0xe07020, 1);
    g.fillEllipse(32, 36, 24, 36);
    g.fillStyle(0xf0c14a, 1);
    g.fillEllipse(32, 42, 12, 18);
    return;
  }
  g.fillStyle(0x8a4b24, 1);
  g.fillEllipse(32, 38, 30, 34);
  g.fillStyle(0xc4783a, 1);
  g.fillEllipse(32, 36, 18, 22);
  g.fillStyle(0x5c3a22, 1);
  g.fillEllipse(32, 22, 26, 14);
}

/** Queue any hub icon the texture manager does not already have. Boot calls this from preload. */
export function queueHubIcons(scene: Phaser.Scene): void {
  for (const icon of missingHubIcons(scene.textures)) {
    scene.load.image(icon.key, icon.path);
  }
}

/**
 * Resolves when the four hub icons are in the texture manager.
 * A miss is tried again, then a painted fallback is installed so scenes can draw.
 */
export function ensureHubIcons(scene: Phaser.Scene): Promise<void> {
  const run = (attempt: number): Promise<void> => {
    const left = missingHubIcons(scene.textures);
    if (left.length === 0) return Promise.resolve();
    if (attempt > MAX_ATTEMPTS) {
      installHubIconFallbacks(scene);
      return Promise.resolve();
    }
    return loadOnce(scene, left, attempt).then(() => run(attempt + 1));
  };
  return run(1);
}

function loadOnce(scene: Phaser.Scene, left: (typeof HUB_ICONS)[number][], attempt: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      scene.load.off('complete', onComplete);
      window.clearTimeout(timer);
      resolve();
    };
    const onComplete = () => finish();
    const timer = window.setTimeout(finish, ATTEMPT_MS);
    scene.load.on('complete', onComplete);
    // A load already in flight has to finish before another batch starts.
    if (!scene.load.isReady()) return;
    for (const icon of left) {
      if (scene.textures.exists(icon.key)) continue;
      scene.load.image(icon.key, hubIconUrl(icon.path, attempt));
    }
    scene.load.start();
  });
}
